import i18n, { translateBlockValue } from 'i18n';
import _ from 'lodash';
import { UNKNOWN_DATUM } from 'business/constants';
import {
    DataTypes as DT,
    TimeScales as TS,
    SlidingTimeScales as STS,
    ChartSettingsT,
} from 'business/definitions';
import { MONTHS, QUARTERS } from 'helpers/dateHelper';
import numbro from 'numbro';

/**
 * Function used to get a month and a year from a string date
 */
function getYearMonthAndDay(value: string | number | Date): {
    year: number;
    month: number;
    day: number;
} {
    const date =
        value instanceof Date
            ? value
            : new Date(parseInt(value?.toString(), 10));
    return {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
    };
}

/**
 * Function used to format a string into a date
 */
export function formatDate(value: string | Date): string {
    if (value instanceof Date) return value.toLocaleDateString(i18n.language);
    const timestamp = parseInt(value, 10);
    return new Date(timestamp).toLocaleDateString(i18n.language);
}

/**
 * Function used to format a string to display a month
 */
export function formatMonth(value: string): string {
    const { month, year } = getYearMonthAndDay(value);
    const monthToString = MONTHS[month];
    return `${monthToString} ${year}`;
}

/**
 * Function used to format a string to display a quarter
 */
export function formatQuarter(value: string): string {
    const { month, year } = getYearMonthAndDay(value);
    const quarterToString = QUARTERS[month];
    return `${quarterToString} ${year}`;
}

export function formatSchoolYear(value: string): string {
    const { year } = getYearMonthAndDay(value);
    return `${year} - ${year + 1}`;
}

/**
 * Function used to format a string to display a Year
 */
export function formatYear(value: string): string {
    const { year } = getYearMonthAndDay(value);
    return year.toString();
}

const parseDateElement = (value: number) => {
    const valueString = value.toString();
    return valueString.length === 1 ? `0${valueString}` : valueString;
};

const formatSlidingDate = (
    startDate: string | number | Date,
    endDate: Date,
) => {
    const { year, month, day } = getYearMonthAndDay(startDate);
    const {
        year: endYear,
        month: endMonth,
        day: endDay,
    } = getYearMonthAndDay(endDate);
    return `${parseDateElement(day)}/${parseDateElement(
        month + 1,
    )}/${year} - ${parseDateElement(endDay)}/${parseDateElement(
        endMonth + 1,
    )}/${endYear}`;
};

export function formatSlidingWeek(value?: string | number | Date): string {
    if (value === undefined) {
        return UNKNOWN_DATUM;
    }
    const dateAWeekLater =
        value instanceof Date
            ? new Date(value)
            : new Date(parseInt(value.toString(), 10));
    dateAWeekLater.setDate(dateAWeekLater.getDate() + 6);
    return formatSlidingDate(value, dateAWeekLater);
}

export function formatSlidingYear(value?: string | number | Date): string {
    if (value === undefined) {
        return UNKNOWN_DATUM;
    }
    const dateAYearLater =
        value instanceof Date
            ? new Date(value)
            : new Date(parseInt(value.toString(), 10));
    dateAYearLater.setFullYear(dateAYearLater.getFullYear() + 1);
    dateAYearLater.setDate(dateAYearLater.getDate() - 1);
    return formatSlidingDate(value, dateAYearLater);
}

/**
 * Format a data point from backend to frontend value
 */
export function formatData(
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    type: DataTypesT,
): number | string {
    if (value == null || value === UNKNOWN_DATUM) {
        return UNKNOWN_DATUM;
    }
    switch (type) {
        case DT.integer:
        case DT.year:
            return parseInt(value, 10);
        case DT.float:
        case DT.money:
            return parseFloat(value);
        case DT.date:
        case DT.datetime: {
            const dateValue = new Date(value);
            if (dateValue) {
                // the next lines seems equivalent to dateValue.setHours(0)
                const userOffset = dateValue.getTimezoneOffset() * 60000;
                const utc = new Date(dateValue.getTime() + userOffset);
                return utc.getTime();
            }
            return UNKNOWN_DATUM;
        }
        case DT.time: {
            // Hours are stored as timestamp. We need to add 1970-01-01 to
            // get a correct value.
            // new Date(dateString) is misinterpreted in Firefox compared to Chrome
            // Thus we convert it first to millisecondes
            const dateValue = new Date(Date.parse(`01/01/1970 ${value} GMT`));
            return dateValue.getTime();
        }
        case DT.coordinates:
            return value
                .replace('POINT(', '')
                .replace(')', '')
                .replace(' ', ' - ');
        case DT.polygon: {
            return Array.isArray(value) ? value : JSON.parse(value).coordinates;
        }
        case DT.category:
        case DT.text:
            return value;
        default:
            return value;
    }
}

/**
 * Flats the map data to handle multi polygons plots.
 * Keeps track of the position of each item in the raw data provided.
 */
export function flattenPolygonData(
    data: Array<ResponseDataT>,
    xKey: string,
    yKey: string,
): [Record<string, unknown>[], number[]] {
    const flatData: Record<string, unknown>[] = [];
    const flatDataRootIndexes: number[] = [];
    data.forEach((item, index) => {
        if (typeof item[xKey] === 'object') {
            // Handle case of unknown polygon
            // @ts-expect-error [TS migration] (previously $FlowFixMe)
            item[xKey].forEach((coordinates) => {
                const flatItem = {
                    [xKey]: coordinates,
                    [yKey]: item[yKey],
                };
                flatData.push(flatItem);
                flatDataRootIndexes.push(index);
            });
        } else {
            flatData.push(item);
            flatDataRootIndexes.push(index);
        }
    });
    return [flatData, flatDataRootIndexes];
}

function translateKeys(input: string): string {
    return typeof input === 'string'
        ? input
              .split('|')
              .map((el) => translateBlockValue(el))
              .join('-')
        : '';
}

// taken from
// https://stackoverflow.com/questions/4467539/javascript-modulo-gives-a-negative-result-for-negative-numbers
function positiveModulo(number: number, modulo: number): number {
    return ((number % modulo) + modulo) % modulo;
}

/**
 * Format the data to display it. Removes any useless figure and
 * uses an order of magnitude to shorten string.
 */
export function formatDisplay(
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    type: DataTypesT,
    decimalPrecision?: 0 | 1 | 2,
    displayBy?: ChartSettingsT['displayBy'],
    // TODO: use object?
    scale?: TimeScalesT,
): string {
    // TODO: factorize with src/ui/AgGridReact/formatters.js
    if (value == null || value === UNKNOWN_DATUM || value === '-') {
        return '-';
    }

    const numbroParameter: numbro.Format = {
        trimMantissa: true,
        optionalMantissa: false,
        spaceSeparated: true,
        thousandSeparated: true,
        mantissa: 2,
    };

    if (decimalPrecision !== undefined) {
        numbroParameter.mantissa = decimalPrecision;
    }

    if (displayBy && (displayBy === 'million' || displayBy === 'thousand')) {
        numbroParameter.forceAverage = displayBy;
    }

    switch (type) {
        case DT.date:
        case DT.datetime: {
            switch (scale) {
                case TS.month:
                    return formatMonth(value);
                case TS.year:
                    return formatYear(value);
                case TS.quarter:
                    return formatQuarter(value);
                case TS.schoolYear:
                    return formatSchoolYear(value);
                case TS.week:
                case STS.slidingWeek:
                    return formatSlidingWeek(value);
                case STS.slidingYear:
                    return formatSlidingYear(value);
                default:
                    return formatDate(value);
            }
        }
        case DT.time: {
            // in the case of stacked bar the value is sometimes not a float which creates
            // an invalid date error
            //
            // it happens when we stack bar with time in the x axis, since stacking time is not
            // officially supported, we changed the type to 'category' to hack it which
            // changes the format of the value to string instead of float
            const dateValue = new Date(parseFloat(value));
            const minutes = dateValue.getMinutes();
            const hours = positiveModulo(
                dateValue.getHours() + dateValue.getTimezoneOffset() / 60,
                24,
            );
            const hourString = `0${Math.floor(hours).toString()}`.slice(-2);
            const minuteString = `0${Math.floor(minutes).toString()}`.slice(-2);
            return `${hourString}:${minuteString}`;
        }
        case DT.integer: {
            const number = parseInt(value, 10);
            return numbro(number).format(numbroParameter);
        }
        case DT.year: {
            const number = parseInt(value, 10);
            return number.toString();
        }
        case DT.float: {
            const floatValue = parseFloat(value);
            return numbro(floatValue).format(numbroParameter);
        }
        case DT.fraction: {
            const floatValue = parseFloat(value);
            const percentNumbroParameter: numbro.Format = {
                trimMantissa: true,
                spaceSeparated: true,
                thousandSeparated: true,
                mantissa: 2,
                output: 'percent',
            };
            if (decimalPrecision !== undefined) {
                percentNumbroParameter.mantissa = decimalPrecision;
            }
            return numbro(floatValue).format(percentNumbroParameter);
        }
        case DT.money: {
            const floatValue = parseFloat(value);
            numbroParameter.trimMantissa = false;
            numbroParameter.optionalMantissa = true;
            return numbro(floatValue).formatCurrency(numbroParameter);
        }
        case DT.category: {
            const labelValue = value.label != null ? value.label : value;
            const translated = translateKeys(labelValue);
            return translated;
        }
        case DT.text:
            return value;
        case DT.polygon:
            return 'Polygone'; // Necessary to avoid table crashing
        default:
            return value;
    }
}

type PlainifyDatum<T extends ResponseDataValueT> = T extends object
    ? T extends Date
        ? T
        : string
    : T;

export type Plainify<DataT extends ResponseDataT> = {
    [K in keyof DataT]: K extends 'filter' ? DataT[K] : PlainifyDatum<DataT[K]>;
};

const isPlain = (
    v: ResponseDataValueT | Record<string, number[]>,
): v is PlainResponseDataValueT => !_.isObject(v);

export const isPolygon = (
    datum: ResponseDataValueT | Record<string, number[]>,
): datum is PolygonData => {
    return (
        Array.isArray(datum) &&
        Array.isArray(datum[0]) &&
        Array.isArray(datum[0][0]) &&
        datum[0][0].every((coord) => Array.isArray(coord) && coord.length === 2)
    );
};
/**
 * Formats the value of the data to transform any object value to a string value.
 * @param datapoint The data object that will be transformed.
 * @param processingFn The transformation function to apply to each value.
 */
const plainifyResponseData = <DataT extends ResponseDataT>(
    datapoint: DataT,
    processingFn: (
        value: DictAllDataValueT | Record<string, number[]>,
    ) => string | number[],
): Plainify<DataT> =>
    Object.fromEntries(
        Object.entries(datapoint).map(([k, v]) => {
            if (k === 'filter') return [k, v];
            if (
                v !== null &&
                !isPlain(v) &&
                !isPolygon(v) &&
                v.label !== undefined
            ) {
                return [k, processingFn(v)];
            }
            return [k, v];
        }),
    );
/** Take care of value formatting - namely unpack `label` if value is of the form {value, label} */
export const unpackLabelFromResponseData = <DataT extends ResponseDataT>(
    datapoint: DataT,
): Plainify<DataT> => plainifyResponseData(datapoint, (v) => v.label);

export const stringifyJsonFromResponseData = <DataT extends ResponseDataT>(
    datapoint: DataT,
): Plainify<DataT> => plainifyResponseData(datapoint, (v) => JSON.stringify(v));

export const manageUnknownCellValue = <
    DataT extends Omit<ResponseDataT, 'filter'>,
>(
    rowData: DataT,
    replacementValue: string = '',
): DataT =>
    // @ts-expect-error [typing useGetDataQuery] 'unknown' is a string when the value is probably something else
    _.mapValues(rowData, (value) =>
        value === 'unknown' ? replacementValue : value,
    );

/**
 * Return a slug version of a string
 * Examples:
 * 'The Quick Brown Fox Jumps Over The Lazy Dog! ' => 'the_quick_brown_fox_jumps_over_the_lazy_dog'
 * 'söme stüff with áccènts' => 'some_stuff_with_accents'
 */
export const slugify = (str: string) =>
    str
        .normalize('NFKD') // split accented characters into their base characters and diacritical marks
        .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
        .trim() // trim leading or trailing whitespace
        .toLowerCase() // convert to lowercase
        .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
        .replace(/\s+/g, '_'); // replace spaces with underscores

export const currencyFormatter = (
    value: number,
    options?: {
        compact?: boolean;
        maximumFractionDigits?: number;
        showSign?: boolean;
    },
) => {
    const notation = options?.compact ? 'compact' : 'standard';
    const maximumFractionDigits = options?.maximumFractionDigits ?? 2;
    const showSign = options?.showSign ?? false;
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        notation,
        maximumFractionDigits,
        signDisplay: showSign ? 'always' : 'auto',
    }).format(value);
};

export const percentageFormatter = (
    value: number,
    options?: {
        maximumFractionDigits?: number;
        showSign?: boolean;
    },
) => {
    const maximumFractionDigits = options?.maximumFractionDigits ?? 2;
    const showSign = options?.showSign ?? false;
    return new Intl.NumberFormat('fr-FR', {
        style: 'percent',
        maximumFractionDigits,
        signDisplay: showSign ? 'always' : 'auto',
    }).format(value);
};

/**
 * Return a formatted float value
 * Examples:
 *  453452,13423 => 453 452,13
 */
export const formatFloatValue = (value: number) => {
    return formatDisplay(value, 'float', 2);
};

/**
 * Add a 's' to the end of a word (or its components) if it is not already plural
 * Note: doesn't work if a word's plural ends with "x"
 * Examples:
 *  - avion => avions
 *  - porte-avion => porte-avions
 *  - porte avions => portes avions
 *  - /!\ chou => chous
 *  - programme de ligne budgétaire => programmes de lignes budgétaires
 * @param word (or compounded word) to pluralize
 * @returns the stupidly pluralized word
 */
export const pluralize = (word: string) => {
    if (!word?.trim()) return '';
    return word
        .trim()
        .split(' ')
        .map((w) => (w.endsWith('s') || w === 'de' ? w : `${w}s`))
        .join(' ');
};
